import apiClient from './client';
import { buildQueryString, type BaseListParams } from './utils';
import type { PaginatedResponse } from '../../types';

// ============================================================================
// Types for User Management (MED-151)
// ============================================================================

// Internal user for dropdowns (simple format)
export interface InternalUser {
  id: string;
  name: string;
  email: string;
  job_title: string | null;
}

// Internal personas for User Management
export type InternalPersona = 'internal_admin' | 'internal_user';

// User status for filtering
export type UserStatus = 'active' | 'inactive' | 'all';

// Internal user with full details for User Management list
export interface InternalUserDetails {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  job_title: string | null;
  department: string | null;
  phone: string | null;
  avatar_url: string | null;
  bio: string | null;
  timezone: string;
  preferences: Record<string, unknown>;
  organization_id: string | null;
  is_active: boolean;
  email_verified_at: string | null;
  last_login_at: string | null;
  created_at: string;
  updated_at: string;
  persona: InternalPersona;
  persona_display: string;
  // Invitation status fields
  invitation_sent_at: string | null;
  invitation_expires_at: string | null;
}

// List params for User Management
export interface UserListParams extends BaseListParams {
  status?: UserStatus;
  persona?: InternalPersona;
}

// Response for user list with counts
export interface UserListResponse extends PaginatedResponse<InternalUserDetails> {
  totalActive: number;
  totalInactive: number;
}

// Input for creating a new internal user
export interface CreateInternalUserInput {
  email: string;
  first_name: string;
  last_name: string;
  persona: InternalPersona;
  job_title: string;
  department: string;
}

// Input for updating an internal user
export interface UpdateInternalUserInput {
  job_title?: string;
  department?: string;
  persona?: InternalPersona;
}

// Departments list — four-function taxonomy (MED-833)
export const DEPARTMENTS = [
  'Grow',
  'Build',
  'Deliver',
  'Sustain / Biz Ops',
] as const;

// User roles list
export const USER_ROLES = [
  'Admin',
  'Cohort Manager',
  'Head of Delivery',
  'Head of Coaching',
  'Head of Curriculum',
  'Head of Sales / GTM',
  'Head of Renewals',
  'Head of Partner Management',
  'Head of Program Ops',
  'Delivery Program Manager',
  'Delivery Program Associate',
  'Sales Associate',
] as const;

// ============================================================================
// API Methods
// ============================================================================

export const usersApi = {
  /**
   * Get all internal users (for team assignment dropdowns)
   */
  async listInternal(): Promise<InternalUser[]> {
    return apiClient.get<InternalUser[]>('/api/users/internal');
  },

  // ==========================================================================
  // User Management Methods (MED-151)
  // ==========================================================================

  /**
   * List all internal users with full details for User Management page
   */
  async list(params?: UserListParams): Promise<UserListResponse> {
    const url = `/api/users${buildQueryString(params)}`;
    const response = await apiClient.rawGetWithMeta<InternalUserDetails[]>(url);

    const defaultPagination = {
      page: 1,
      limit: 25,
      total: response.data.length,
      totalPages: 1,
    };

    const pagination = response.meta?.pagination as typeof defaultPagination | undefined;

    return {
      data: response.data,
      pagination: pagination ?? defaultPagination,
      totalActive: (response.meta?.totalActive as number) || 0,
      totalInactive: (response.meta?.totalInactive as number) || 0,
    };
  },

  /**
   * Get a single internal user by ID
   */
  async getById(id: string): Promise<InternalUserDetails> {
    return apiClient.get<InternalUserDetails>(`/api/users/${id}`);
  },

  /**
   * Create a new internal user (Admin or Cohort Manager)
   */
  async create(data: CreateInternalUserInput): Promise<InternalUserDetails> {
    return apiClient.post<InternalUserDetails>('/api/users', data);
  },

  /**
   * Update an internal user's profile
   */
  async update(id: string, data: UpdateInternalUserInput): Promise<InternalUserDetails> {
    return apiClient.put<InternalUserDetails>(`/api/users/${id}`, data);
  },

  /**
   * Deactivate a user
   */
  async deactivate(id: string): Promise<{ id: string; is_active: boolean }> {
    return apiClient.post<{ id: string; is_active: boolean }>(`/api/users/${id}/deactivate`, {});
  },

  /**
   * Activate a user
   */
  async activate(id: string): Promise<{ id: string; is_active: boolean }> {
    return apiClient.post<{ id: string; is_active: boolean }>(`/api/users/${id}/activate`, {});
  },

  /**
   * Check if an email is already in use
   */
  async checkEmail(email: string): Promise<{ exists: boolean }> {
    return apiClient.get<{ exists: boolean }>(`/api/users/check-email?email=${encodeURIComponent(email)}`);
  },

  /**
   * Resend invitation email to a user
   */
  async resendInvitation(id: string): Promise<{ id: string }> {
    return apiClient.post<{ id: string }>(`/api/users/${id}/resend-invitation`, {});
  },
};
